import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useImmerReducer } from "use-immer";

import { assertNativeError, delayForIndexing } from "@examspark/common";
import {
  CautionBanner,
  ConfirmModal,
  Modal,
  ModalProps,
  SubmitButtons,
} from "@examspark/common-ui";
import { SaveResult } from "@examspark/types";
import { LtiSdkLaunchOptions } from "@examspark/types/dist/components/activity-editor";

import { useQueryGetExamConfigCountForExamId } from "../../../api";
import { useModal } from "../../../hooks/useModal";
import { logger } from "../../../util";
import {
  defaultExamEditorState,
  ExamEditorContext,
  examEditorReducer,
  ExamEditorState,
} from "../../exam-editor/examEditor";

import ActivityEditor, { ActivityEditorConfig } from "./ActivityEditor";
import { ExamBuilderProps, useExamBuilder } from "./hooks/useExamBuilder";

// Add tabOverride prop type
export type TabOverride = {
  items?: React.ReactNode;
  basic?: React.ReactNode;
  preview?: React.ReactNode;
};

type EditorProps = ModalProps & {
  onSave?: (result: SaveResult) => unknown | Promise<unknown>;
  // this is redundant with the one in the ExamBuilderProps, need to consolidate
  editorConfig: ActivityEditorConfig &
    Pick<LtiSdkLaunchOptions, "contentFilter" | "createWithTags">;
  tabOverride?: TabOverride;
  warnOnChangeIfAssignmentsExist?: boolean;
  showPreferences?: boolean;
} & ExamBuilderProps;

// The 'Common' implementation of the Activity Editor.
const ActivityEditorModal = (props: EditorProps) => {
  const [newActivityState, dispatchNewActivity] =
    useImmerReducer<ExamEditorState>(examEditorReducer, defaultExamEditorState);
  return (
    <ExamEditorContext.Provider value={[newActivityState, dispatchNewActivity]}>
      <ActivityEditorModalInner {...props} />
    </ExamEditorContext.Provider>
  );
};

const ActivityEditorModalInner = ({
  onSave,
  tabOverride,
  ...props
}: EditorProps) => {
  const [activityState] = useContext(ExamEditorContext);
  const [isSaving, setIsSaving] = useState(false);

  const client = useQueryClient();
  const examBuilder = useExamBuilder({
    examId: props.examId,
    onBeforeSave: props.onBeforeSave,
    config: props.editorConfig,
  });
  const nameModal = useModal();
  const needFullscreen = useMediaQuery({ maxWidth: "600px" });

  const examConfigCountQuery = useQueryGetExamConfigCountForExamId({
    examId: props.examId,
  });

  const examConfigTotal = examConfigCountQuery.data?.total;

  // Tried adding this logic directly into the useExamBuilder hook, but it
  // didn't like having the ConfirmModal inside of it.
  const checkExamConfigCount = async () => {
    if (!props.warnOnChangeIfAssignmentsExist) return true;
    if (!examConfigTotal) return true;

    if (examConfigTotal) {
      const result = await ConfirmModal.show({
        children: (
          <CautionBanner variant="plain">
            This assessment has already been assigned {examConfigTotal} time
            {examConfigTotal === 1 ? "" : "s"}. Changes to the content of this
            assessment can cause inconsistencies in student submissions and
            reporting. Are you sure you want to save these changes?
          </CautionBanner>
        ),
      });

      return result;
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      if (props.examId == null) {
        if (!activityState.name) {
          throw new Error("Please enter a Title.");
        }
        if (
          !activityState.subject &&
          props.editorConfig?.fields?.subject?.required
        ) {
          throw new Error("Please select a Subject.");
        }
      }

      if (examConfigTotal) {
        const confirm = await checkExamConfigCount();
        if (!confirm) {
          setIsSaving(false);
          return;
        }
      }

      const result = await examBuilder.handleSave({
        tags: props.editorConfig?.createWithTags,
      });

      if (result != null) {
        client.invalidateQueries(["playable-exam"]);
        await delayForIndexing();
        nameModal.close();
        onSave?.(result);
        logger.info("Exam Builder: Save", {
          exam: result,
        });
      }
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }

    setIsSaving(false);
  };

  return (
    <>
      <Modal
        title="Assessment Editor"
        size={needFullscreen ? "max" : "full"}
        onAfterClose={() => {
          examBuilder.handleReset();
        }}
        buttons={
          <SubmitButtons
            submitLabel="Save Assessment"
            onCancel={props.onRequestClose}
            onSubmit={(e) => {
              if (e.altKey) {
                console.log("Activity State", activityState);
                return;
              }

              handleSave();
            }}
            isSubmitting={examBuilder.isCreating || isSaving}
          />
        }
        {...props}
      >
        <ActivityEditor
          initialTab="items"
          config={props.editorConfig}
          tabOverride={tabOverride}
          showPreferences={props.showPreferences}
        />
      </Modal>
    </>
  );
};

export default ActivityEditorModal;
